/**
 * Monochromatic / near-monochromatic palette.
 *
 * Single hue family (cool blue-gray) with values shifted by lightness and a
 * narrow hue arc so zones remain distinguishable without becoming a rainbow.
 * Per docs/DECISIONS.md ADR-007 and the user's "more subtle" directive.
 *
 * Touch this file when adjusting the look. Do not hard-code zone colors
 * anywhere else.
 */
import type { Zone } from '../types/domain';

export const ZONE_COLORS: Record<Zone, string> = {
  // All values within HSL roughly (200-220°, 10-30% saturation, 55-78% lightness)
  'standing':            '#c7d3df', // anchor: warmest, lightest
  'clinch':              '#b9c5d3',

  'guard-top':           '#9aaec0',
  'guard-bottom':        '#a8becf',

  'half-guard-top':      '#94a6b8',
  'half-guard-bottom':   '#a3b6c8',

  'side-control-top':    '#8fa0b2',
  'side-control-bottom': '#9eafc1',

  'mount-top':           '#8b9caf',
  'mount-bottom':        '#9aabbd',

  'back-top':            '#869ab0',
  'back-bottom':         '#94a8bc',

  'turtle-top':          '#9caebf',
  'turtle-bottom':       '#aabbcb',

  'north-south-top':     '#8597a9',
  'north-south-bottom':  '#94a6b7',

  // Unsuffixed zones — a node that names a situation without asserting a
  // side. Tinted between the top and bottom values of their own family so
  // the relationship reads, and deliberately duller: an unsuffixed zone is
  // usually a node whose side has not been settled yet, not a third
  // category that earned its own colour. If these start standing out on
  // the map, that is the cue to go and decide their sides.
  'guard':               '#a1b6c8',
  'half-guard':          '#9db2c4',
  'back':                '#8fa6ba',
  'mount':               '#94a5b8',

  // The standing split, either side of the 'standing' anchor by lightness.
  'standing-top':        '#cfdae5',
  'standing-bottom':     '#bccbd9',

  'submitted':           '#6c7785', // distinct dim — terminal sink
  'scrambled':           '#a7adb6',
};

export const BG_COLOR = '#070810';
export const HUB_GLOW = 1.4;
export const BRIDGE_RING = '#d3dde8';

// Signal gold — the treatment's brand / primary-action colour. Currently
// used ONLY for the front-door "Open the map" CTA (per the scoped design
// decision to keep gold off the rest of the app for now; the existing
// cyan `#9be7ff` stays the in-app action accent). Also feeds the cluster-
// colour reserved-hue guard so no family/concept swatch collides with it.
export const BRAND_GOLD = '#e0b24a';
// Color-brightness multiplier applied to orphan (degree-0) node base
// colors in Nodes.tsx. Used to be a radius multiplier which made
// orphans sub-pixel; now it's purely visual quieting, so the value
// was lifted from 0.32 → 0.55. Still clearly muted vs connected
// nodes, but not so dark the node loses its zone-color identity.
export const ORPHAN_ALPHA = 0.55;

/**
 * The dim level at which every surface equals the value it was TUNED to.
 *
 * A reference point, not a preference — deliberately not the value the
 * app actually uses, which is FOCUS_DIM below. It is easy to read this
 * as "the default" and re-point it when the dim changes; doing
 * that would move the pivot the other three surfaces scale around, so
 * the bridge ring and both label styles would silently stop matching
 * whatever the sphere is doing.
 *
 * There is no reason to touch this unless the four tuned constants are
 * re-tuned together, and even then, scaling all four by the same factor
 * as this anchor is a mathematical no-op: dimmedValue divides by it.
 */
export const TUNED_FOCUS_DIM = 0.05;

/**
 * How much brightness a node keeps when something ELSE is selected.
 *
 * 0.01 — un-connected nodes hold 1% of their colour, effectively black.
 * Arrived at by exposing it as a slider, driving it against the real map
 * and keeping what read best; the slider is gone because tuning it is not
 * a decision to hand a visitor, but the value it produced is the whole
 * reason this is not still 0.05.
 *
 * Distinct from TUNED_FOCUS_DIM above, which is only the reference point
 * the other three surfaces scale around. Change THIS one to re-balance
 * the map; the ring and both label styles follow automatically.
 */
export const FOCUS_DIM = 0.01;

/**
 * Apply the focus slider to one surface's own tuned dim value.
 *
 * Four things recede when something is selected — the sphere's colour,
 * its bridge ring, its proximity label and its hub label — and each was
 * tuned separately, in different units. A colour multiplier of 0.05 and a
 * text opacity of 0.06 are not the same number by coincidence; they are
 * the values at which each of those surfaces looked equally far back.
 *
 * So the slider does not overwrite them. It scales each one by how far
 * the slider sits from TUNED_FOCUS_DIM, which preserves the relationships
 * between them while moving all four together. Endpoints stay honest: at
 * the anchor every surface lands on exactly the value it was tuned to, at
 * 0 everything vanishes, and nothing can be pushed brighter than its own
 * undimmed state.
 *
 * `lit` is that undimmed state, and it differs per surface — a hub label
 * rests at 0.78, a bridge ring at 0.5 — which is why it is a parameter
 * rather than 1.
 */
export function dimmedValue(tuned: number, focusDim: number, lit = 1): number {
  return Math.min(lit, tuned * (focusDim / TUNED_FOCUS_DIM));
}

// Counter / interception accent (muted warm — only non-blue in the palette)
export const COUNTER_COLOR = '#9e7a5c';

// Edge tint anchors
export const EDGE_DIM = '#4a5563';
export const EDGE_BRIGHT = '#b3c2d3';

// The protagonist (center) — slightly warm, lightest in the scene
export const CENTER_COLOR = '#e6dfcf';
export const CENTER_HALO = '#c6b58c';

// Stars / dust
export const STAR_COLOR = '#cfd6e0';
export const STAR_ALPHA = 0.55;

// Cluster tints — applied to node sphere color AND bridge ring when a
// clusterBy attribute is active. Muted saturations so they sit alongside
// the base zone palette rather than dominating.
export const CLUSTER_COLORS: Record<string, string> = {
  // position type
  top: '#d4a070',
  bottom: '#7aaec0',
  neutral: '#b3b6bc',
  terminal: '#a48eb0',
  // 'flux' = scrambled / momentary chaotic state — its own warm-yellow
  // so it reads as "in motion" rather than collapsed into neutral.
  flux: '#e0c468',
  // move category
  sweep: '#7aaec0',
  submission: '#c47d8c',
  pass: '#d4a070',
  escape: '#7dc48a',
  takedown: '#a87dc4',
  transition: '#a3a8b0',
  'guard-retention': '#7da8c4',
  // submission type
  choke: '#c47d8c',
  'joint-lock': '#d4b070',
  'leg-lock': '#7d8cc4',
  'neck-crank': '#a8c47d',
  other: '#a3a8b0',
  // disposition (move-mode only). Derived from category: escape +
  // guard-retention → defensive; submission/sweep/takedown/pass →
  // offensive; transition → transitional.
  defensive: '#7dc48a',
  offensive: '#d47a7a',
  transitional: '#a3a8b0',
  // posture (position body shape). Visual mnemonic: vertical/upright
  // postures lean warm, ground-bound postures lean cool, transitional
  // shapes go muted.
  standing: '#d4a070',
  seated: '#c4a87d',
  supine: '#7aaec0',
  prone: '#7d8cc4',
  turtle: '#a87dc4',
  inverted: '#c47d8c',

  // advantage — who is winning. `neutral` already exists above (position
  // type uses the same word for the same idea) so only these two are new;
  // without them the whole axis fell through to CLUSTER_UNCATEGORIZED and
  // rendered uniformly grey, which made the lens look broken.
  //
  // Warm for winning, cool for losing, deliberately echoing the drawer's
  // advantage tags so the two agree — see ADVANTAGE_STYLE in
  // ui/tag-labels.ts. A touch more saturated than the position-type pair
  // it sits next to, because the interesting thing about this lens is
  // exactly where it DISAGREES with geography: 41 nodes are bottom and
  // winning, 9 are top and losing, and switching between the two lenses
  // should make those 50 nodes visibly change hands.
  tori: '#e0a35c',
  uke: '#6b9fd4',
};

// Neutral fallback when a node has no cluster assignment for the active
// clusterBy attribute (e.g. clustering by submissionType but the move is a
// sweep). The node is grayed out a touch so it visually recedes.
export const CLUSTER_UNCATEGORIZED = '#5e636b';

/**
 * Deterministic color for OPEN-ENDED cluster ids (`family` / `concept`)
 * that aren't in the fixed CLUSTER_COLORS table — free-form strings like
 * 'saddle', 'k-guard', 'kuzushi', 'framing'.
 *
 * Two-stage strategy (see docs/DECISIONS.md ADR-039):
 *
 *   1. PREFERRED — golden-angle ALLOCATION over the full universe of ids.
 *      `initClusterColors(families, concepts)` is called once after data
 *      load; it ranks every distinct id lexicographically across the WHOLE
 *      dataset (not the filtered subset) and hands each a hue at
 *      `rank * 137.508° (mod 360)`. The golden angle guarantees maximal
 *      hue spread for any N, so N clusters stay separated instead of
 *      bunching. Determinism comes from the stable lexicographic rank, so
 *      a given id keeps its colour when filters change.
 *
 *   2. FALLBACK — the original FNV-1a hash (`fnvFallback`), used only for
 *      ids not present at init time (e.g. a tag added live). Stateless but
 *      collision-prone; acceptable as a defensive last resort.
 *
 * Both stages hold S≈50 / L≈62 (inside the shipped 42–57 / 58–65 band) so
 * open-ended tints belong to the same muted palette as the fixed table,
 * and both dodge the reserved hues (fixed-table tints + brand gold +
 * terminal sink + cream protagonist) so a cluster is never mistaken for
 * one of those.
 */

// Per-axis rank tables, populated by initClusterColors. rank → hue via the
// golden angle. Empty until init runs (fallback covers that window).
let RANKED: Record<'family' | 'concept', Map<string, number>> = {
  family: new Map(),
  concept: new Map(),
};

/**
 * Call ONCE after the dataset loads (App.tsx, post data-load Promise.all).
 * Ranks every distinct family + concept id across the full dataset,
 * lexicographically, so a given id's rank — and therefore its colour — is
 * stable regardless of which subset the current filters leave visible.
 */
export function initClusterColors(families: string[], concepts: string[]): void {
  const rank = (ids: string[]) =>
    new Map([...new Set(ids)].sort().map((id, i) => [id, i] as const));
  RANKED = { family: rank(families), concept: rank(concepts) };
}

/**
 * rank → hue on the golden angle (137.508°). This is the MOST uniform
 * sequence possible: for N ids the minimum pairwise hue gap is ~360/N,
 * so clusters stay maximally separated.
 *
 * NOTE — reserved-hue nudge intentionally dropped (deviation from the
 * brief's suggested `allocatedHue`). Empirically the nudge-loop
 * (`+18° whenever within 12° of a reserved hue`) DESTROYED the golden
 * angle's even spacing — it dropped the 15-concept minimum gap from a
 * clean 12.0° to ~1° and even produced exact collisions. And the guard
 * buys little in practice: cluster lenses are mutually exclusive (you
 * cluster by concept OR positionType, never both), so a concept tint is
 * never on screen at the same time as the fixed-table `top`/`bottom`
 * swatches it might resemble; the KEY panel labels whatever IS shown.
 * Pure golden angle strictly beats the nudged version on the acceptance
 * criterion ("no two swatches within 12°"), so we keep it pure.
 */
function allocatedHue(rank: number): number {
  return Math.round((rank * 137.508) % 360);
}

/**
 * Public API — unchanged signature except an optional axis arg. Callers
 * that know the axis (`colorForCluster(id, by)`, `clusterColor(id, by)`)
 * pass it through; single-arg callers default to 'concept'.
 */
export function clusterColorForId(
  id: string,
  by: 'family' | 'concept' = 'concept',
): string {
  const r = RANKED[by]?.get(id);
  if (r !== undefined) return hslToHex(allocatedHue(r), 50, 62);
  return fnvFallback(id);
}

/** Original FNV-1a hash → HSL. Defensive fallback for un-ranked ids. */
function fnvFallback(id: string): string {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = (h >>> 0) % 360;
  const sat = 42 + ((h >>> 8) & 0x0f); // 42-57
  const light = 58 + ((h >>> 12) & 0x07); // 58-65
  return hslToHex(hue, sat, light);
}

function hslToHex(h: number, s: number, l: number): string {
  const sf = s / 100;
  const lf = l / 100;
  const c = (1 - Math.abs(2 * lf - 1)) * sf;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) { r = c; g = x; }
  else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; }
  else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; }
  else { r = c; b = x; }
  const m = lf - c / 2;
  const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return '#' + toHex(r) + toHex(g) + toHex(b);
}
